import type { Product } from "schemas/generated/product";

import React from "react";

import { useKeyboardListNavigation } from "@agent-ui/features/chat/components/chat-elements/context-items-menu/useKeyboardListNavigation";
import { Button } from "@replo/design-system/components/button/Button";
import { Empty } from "@replo/design-system/components/empty/Empty";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { Tag } from "@replo/design-system/components/tag/Tag";
import twMerge from "@replo/design-system/utils/twMerge";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { routes } from "@v2/utils/router";
import startCase from "lodash-es/startCase";
import { ImageIcon, Search } from "lucide-react";
import InfiniteScroll from "react-infinite-scroll-component";
import { generatePath, Link } from "react-router-dom";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

const ProductContextItemSkeleton: React.FC = () => {
  return (
    <div className="flex flex-row gap-1 px-2 py-1.5 items-center rounded-lg w-full">
      <Skeleton className="h-8 w-8 min-h-8 min-w-8 flex-shrink-0 rounded-md" />
      <div className="flex flex-col w-full pl-1 gap-1">
        <Skeleton className="h-3 w-21" />
        <Skeleton className="h-3 w-52" />
      </div>
    </div>
  );
};

const ProductContextItem = React.forwardRef<
  HTMLButtonElement,
  {
    product: Product;
    onProductSelect: (product: Product) => void;
    isFocused?: boolean;
  }
>(({ product, onProductSelect, isFocused = false }, ref) => {
  let productImage = product.featuredMedia?.url;
  if (product.media?.[0]?.type === "image") {
    productImage = product.media[0].url;
  }

  const tagVariant: "success" | "info" | "neutral" = exhaustiveSwitch(
    product,
    "status",
  )({
    active: "success",
    draft: "info",
    archived: "neutral",
  });

  return (
    <button
      ref={ref}
      key={product.id}
      className={twMerge(
        "flex flex-row gap-1 p-1.5 py-1 items-center rounded-lg transition-colors cursor-pointer w-full text-left",
        "border border-transparent hover:bg-menu-item-selected/50 focus:outline-none focus:ring-2 focus:ring-primary/50",
        isFocused && "bg-menu-item-selected",
      )}
      onClick={() => {
        onProductSelect(product);
      }}
    >
      {productImage ? (
        <div className="bg-surface h-8 w-8 min-h-8 min-w-8 flex-shrink-0 flex items-center justify-center rounded-md overflow-hidden">
          <img
            src={productImage}
            alt={product.title}
            className="h-full w-full object-cover"
          />
        </div>
      ) : (
        <div className="bg-surface h-8 w-8 min-h-8 min-w-8 flex-shrink-0 flex items-center justify-center rounded-md">
          <ImageIcon className="text-muted size-4" />
        </div>
      )}
      <div className="flex flex-col w-full pl-1 min-w-0">
        <div className="text-xs font-medium truncate" title={product.title}>
          {product.title}
        </div>
        <div className="flex flex-row gap-1 items-center min-w-0">
          <Tag size="sm" variant={tagVariant}>
            {startCase(product.status)}
          </Tag>
          <span className="text-xs text-muted m-0 flex-1 min-w-0 truncate">
            {product.slug}
          </span>
        </div>
      </div>
    </button>
  );
});

ProductContextItem.displayName = "ProductContextItem";

interface ProductContextItemsListProps {
  products: Product[];
  isLoading: boolean;
  fetchNextProductsPage: () => void;
  hasNextProductsPage: boolean;
  onProductSelect: (product: Product) => void;
  searchQuery: string;
  onKeyDown?: (event: React.KeyboardEvent) => void;
}

export const ProductContextItemsList: React.FC<
  ProductContextItemsListProps
> = ({
  products,
  isLoading,
  searchQuery,
  fetchNextProductsPage,
  hasNextProductsPage,
  onProductSelect,
  onKeyDown,
}) => {
  const projectId = useCurrentProjectId();

  const { focusedIndex, handleKeyDown, getItemRef } = useKeyboardListNavigation(
    {
      itemCount: products.length,
      onSelect: (index) => {
        if (products[index]) {
          onProductSelect(products[index]);
        }
      },
      isEnabled: !isLoading && products.length > 0,
    },
  );

  const noProducts =
    products.length === 0 && !isLoading && searchQuery.length === 0;
  const emptySearch =
    products.length === 0 && !isLoading && searchQuery.length > 0;

  const combinedKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      handleKeyDown(event);
      onKeyDown?.(event);
    },
    [handleKeyDown, onKeyDown],
  );

  if (isLoading) {
    return (
      <div className="flex flex-col flex-1">
        <div className="flex-1 overflow-y-auto p-2">
          <div className="flex flex-col gap-1">
            {Array.from({ length: 10 }).map((_, index) => (
              <ProductContextItemSkeleton key={index} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (noProducts) {
    return (
      <div className="flex flex-col flex-1 overflow-hidden h-full pb-3">
        <div className="flex-1 p-2 flex items-center justify-center">
          <Empty
            title="No Products Yet"
            description="Add products to use them in your message."
            size="sm"
            media={
              <img
                src="/images/no-products.svg"
                alt="No products"
                className="h-10 w-10 select-none pointer-events-none"
              />
            }
          >
            {projectId && (
              <Link
                to={generatePath(routes.shops.products.list, { projectId })}
              >
                <Button variant="secondary" size="sm">
                  Add Product
                </Button>
              </Link>
            )}
          </Empty>
        </div>
      </div>
    );
  }

  if (emptySearch) {
    return (
      <div className="flex flex-col flex-1 overflow-hidden h-full pb-3">
        <div className="flex-1 p-2 flex items-center justify-center">
          <Empty
            title="No Products Found"
            description="Try adjusting your search."
            size="sm"
            icon={<Search />}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col flex-1"
      onKeyDown={combinedKeyDown}
      tabIndex={0}
      data-list-container="products"
    >
      <InfiniteScroll
        dataLength={products.length ?? 0}
        next={() => void fetchNextProductsPage()}
        hasMore={hasNextProductsPage}
        loader={
          <div className="px-2">
            <ProductContextItemSkeleton />
          </div>
        }
        scrollableTarget="context-menu-scroll-container"
      >
        <div className="flex flex-col gap-1 p-2 pb-1">
          {products.map((product, index) => {
            return (
              <ProductContextItem
                key={product.id}
                ref={getItemRef(index)}
                product={product}
                onProductSelect={onProductSelect}
                isFocused={index === focusedIndex}
              />
            );
          })}
        </div>
      </InfiniteScroll>
    </div>
  );
};
